import { useQuery } from "@tanstack/react-query";
import { useTRPC } from "@/trpc";
import type { RouterOutputs } from "@/trpc";
import { useMemo } from "react";
import type { WeekKey } from "@/features/time-entry/utils/week-utils";

// Use explicit types from router outputs
export type ExpenseGroup = RouterOutputs["expense"]["getMyExpenses"][number];
export type ExpenseItem = ExpenseGroup["expenses"][number];

/**
 * Hook to fetch expenses for the current provider.
 * Returns expenses grouped by week (Monday-Sunday).
 */
export function useProviderExpenses(params?: {
  weekKey?: WeekKey;
  status?: "pending" | "approved" | "rejected" | "all";
}) {
  const trpc = useTRPC();

  const {
    data: expenseGroups,
    isLoading,
    error,
    refetch,
  } = useQuery(
    trpc.expense.getMyExpenses.queryOptions({
      weekKey: params?.weekKey,
      status: params?.status ?? "all",
    }),
  );

  // Create a map of week keys to expense groups for easy lookup
  const expensesByWeek = useMemo(() => {
    const map = new Map<WeekKey, ExpenseGroup>();
    if (expenseGroups) {
      for (const group of expenseGroups) {
        map.set(group.weekKey, group);
      }
    }
    return map;
  }, [expenseGroups]);

  // Get sorted week keys (most recent first)
  const sortedWeekKeys = useMemo(() => {
    if (!expenseGroups) {
      return [] as string[];
    }
    return expenseGroups.map((g) => g.weekKey);
  }, [expenseGroups]);

  // Calculate totals
  const totalPending = useMemo(() => {
    if (!expenseGroups) {
      return 0;
    }
    return expenseGroups.reduce((sum, g) => sum + g.pendingCount, 0);
  }, [expenseGroups]);

  const totalApproved = useMemo(() => {
    if (!expenseGroups) {
      return 0;
    }
    return expenseGroups.reduce((sum, g) => sum + g.approvedCount, 0);
  }, [expenseGroups]);

  const totalRejected = useMemo(() => {
    if (!expenseGroups) {
      return 0;
    }
    return expenseGroups.reduce((sum, g) => sum + g.rejectedCount, 0);
  }, [expenseGroups]);

  const grandTotal = useMemo(() => {
    if (!expenseGroups) {
      return 0;
    }
    return expenseGroups.reduce((sum, g) => sum + g.totalAmount, 0);
  }, [expenseGroups]);

  return {
    expenseGroups: (expenseGroups ?? []) as ExpenseGroup[],
    expensesByWeek,
    sortedWeekKeys,
    totalPending,
    totalApproved,
    totalRejected,
    grandTotal,
    isLoading,
    error,
    refetch,
  };
}
